import type { RouterScrollBehavior } from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'

import { Routes } from '@/const/Routes'
import { parseSlug } from '@/utils'
import { useAuthStore } from '@/stores/auth'

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    title?: string
  }
}

const setupScrollBehavior = (): RouterScrollBehavior => {
  return (to, _, savedPosition) => {
    if (savedPosition) {
      return { ...savedPosition, behavior: 'smooth' }
    }

    if (to.hash) {
      return { el: to.hash }
    }

    return { top: 0, behavior: 'auto' }
  }
}

export const router = createRouter({
  scrollBehavior: setupScrollBehavior(),
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: Object.values(Routes)
})

router.beforeEach((to, _, next) => {
  const { isAuthenticated, isInAschehougOrganization } = useAuthStore()

  const isUnderDevelopmentPath = to.fullPath.toLowerCase().includes('fag/innhold-under-utvikling')
  const isAtDevRouteWithoutBeingAschehougEmployee = isUnderDevelopmentPath && !isInAschehougOrganization

  const routeRequiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const isAtAuthRouteWithoutBeingAuthenticated = routeRequiresAuth && !isAuthenticated

  if (isAtAuthRouteWithoutBeingAuthenticated) {
    sessionStorage.setItem('modus-redirect-route', to.fullPath)
    next({ name: Routes.Home.name })
  }
  else if (isAtDevRouteWithoutBeingAschehougEmployee) {
    next({ name: Routes.Home.name }) // redirect to home page
  }
  else {
    next() // proceed to the requested route
  }
})

router.afterEach(to => {
  const setViewTitle = (title: string) => `${title} – Aschehoug VGS`
  if (to.meta.title) return setViewTitle(to.meta.title)

  const params = Object.values(to?.params)
  const slug = params[params.length - 1] as string
  setViewTitle(parseSlug(slug))
})
